import type { NodeDisplayState } from './EditorContext';
import type { NodeLiveState } from './useProcessRunSession';
import type { NodeRunSnapshot, ProcessNode } from './graphTypes';
import type { NodeExecutionStatus } from '@api/processes';
import type { RunNodeStatus } from '@api/processRuns';

/**
 * Map the persisted {@link NodeExecutionStatus} to the renderer's
 * {@link NodeDisplayState['status']}. The two share most values; persisted
 * snapshots never carry "waiting" or "skipped" because those are
 * mid-flight states that resolve before the snapshot is committed.
 */
export const persistedStatusToDisplay = (
  s: NodeExecutionStatus,
): NodeDisplayState['status'] => {
  switch (s) {
    case 'running':
      return 'running';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'idle':
    default:
      return 'idle';
  }
};

/**
 * Map the live {@link RunNodeStatus} to the renderer's
 * {@link NodeDisplayState['status']}. The wire enum has a 'pending' value
 * that the badge renders as 'idle' (the run engine queued the node but
 * hasn't started it yet).
 */
export const liveStatusToDisplay = (
  s: RunNodeStatus,
): NodeDisplayState['status'] => {
  switch (s) {
    case 'running':
      return 'running';
    case 'provisioning':
      return 'running';
    case 'waiting_for_runner':
      return 'running';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'skipped':
      return 'skipped';
    case 'pending':
    default:
      return 'idle';
  }
};

/**
 * Resolve the display state for a node, preferring live SSE data over the
 * persisted snapshot embedded on the React Flow node's `data`. Pulled out
 * of `ProcessEditorPage` so the page's cognitive complexity stays bounded
 * — the two if-branches now live in one named, testable place.
 *
 * @param nodeId          Node whose state we're resolving.
 * @param liveStateForNode Lookup that returns the live SSE state or
 *                        `undefined` when the node isn't in the live map.
 * @param nodesById       Map of `nodeId -> ProcessNode` for finding the
 *                        persisted snapshot.
 */
export const resolveNodeDisplayState = (
  nodeId: string,
  liveStateForNode: (id: string) => NodeLiveState | undefined,
  nodesById: ReadonlyMap<string, ProcessNode>,
): NodeDisplayState => {
  const live = liveStateForNode(nodeId);
  if (live) {
    return {
      status: liveStatusToDisplay(live.status),
      errorMessage: live.errorMessage,
      errorDetail: live.errorDetail,
      executionTimeMs: live.executionTimeMs ?? null,
      creditsSpent: live.creditsSpent ?? null,
      source: 'live',
      live,
    };
  }
  const node = nodesById.get(nodeId);
  const snapshot =
    (node?.data as Partial<NodeRunSnapshot> | undefined) ?? null;
  const exec = snapshot?.latestExecution ?? null;
  if (!exec) {
    return {
      status: 'idle',
      errorMessage: null,
      errorDetail: null,
      executionTimeMs: null,
      source: 'none',
    };
  }
  return {
    status: persistedStatusToDisplay(exec.status),
    errorMessage: exec.errorMessage ?? null,
    errorDetail: exec.errorDetail ?? null,
    executionTimeMs: exec.executionTimeMs ?? null,
    creditsSpent: exec.creditsSpent ?? null,
    source: 'persisted',
  };
};
